import { ctx as routes } from "expo-router/_ctx";

import { APP_GROUP_SETTINGS } from "@/lib/auth/appGroupSettings";
import { isPublicRoutePath } from "@/lib/auth/publicRoutePaths";
import { registerVendorChunk } from "@/lib/platform/vendorChunk.web";

const VENDOR_FILE = /^\.\/\(app\)\//;
const APP_LAYOUT = /^\.\/\(app\)\/_layout\.[jt]sx?$/;

let vendorLoaded = false;
let vendorChunk: Promise<void> | null = null;

/** Whether the `(app)` chunk has loaded, so its routes resolve synchronously. */
function vendorChunkLoaded(): boolean {
  return vendorLoaded;
}

/**
 * Loads the `(app)` chunk once and settles either way: a failed load surfaces when a route asks
 * for it, as an `AsyncRequireError` the root error boundary handles.
 */
function loadVendorChunk(): Promise<void> {
  vendorChunk ??= Promise.resolve()
    .then(() => routes("./(app)/_layout.tsx"))
    .then(() => {
      vendorLoaded = true;
    }, () => undefined);
  return vendorChunk;
}

registerVendorChunk({ load: loadVendorChunk, loaded: vendorChunkLoaded });

// Any cold open but a public page renders `(app)` first, so the first lookup, when the router
// builds its tree, starts that chunk alongside the fonts. Not at module load: Expo's chunk loader
// is not installed yet. The navigator and the root layout's gate reuse the request.
let prefetchVendor = !isPublicRoutePath(globalThis.location?.pathname ?? "");

/**
 * Expo Router's route context on web, swapped in by metro.config.js (TRO-1972).
 *
 * Expo Router reads every layout's `unstable_settings` while it builds the route tree. With the
 * stock context that fetched the whole `(app)` chunk on every public page, and still read `(app)`'s
 * settings as missing, because the chunk had not arrived. Until a vendor route first renders, an
 * `(app)` file here is a thenable instead: the tree reads `(app)`'s settings off it, and the chunk
 * is fetched only when a route awaits it.
 */
function webRouteContext(key: string): unknown {
  if (prefetchVendor) {
    prefetchVendor = false;
    void loadVendorChunk();
  }
  if (vendorLoaded || !VENDOR_FILE.test(key)) return routes(key);
  return {
    unstable_settings: APP_LAYOUT.test(key) ? APP_GROUP_SETTINGS : undefined,
    then(resolve: (module: unknown) => unknown, reject?: (error: unknown) => unknown) {
      return Promise.resolve(routes(key))
        .then((module) => {
          vendorLoaded = true;
          return module;
        })
        .then(resolve, reject);
    },
  };
}

export const ctx: typeof routes = Object.assign(webRouteContext, {
  keys: routes.keys,
  resolve: routes.resolve,
  id: routes.id,
});
